/**
 * Dados da modal "Visualizar contexto" (spec 2026-09-20 §5.6).
 *
 * Duas consultas: a LISTA de execuções em que o nó recebeu contexto e, para a
 * execução escolhida, o que chegou em cada passagem.
 *
 * Os dois hooks só buscam com `enabled` — a modal fechada não pede nada
 * (CLAUDE.md NUNCA #14). E nenhum dos dois leva `refetchInterval`: uma foto
 * gravada numa execução passada não muda mais. A barra lateral do histórico
 * atualiza sozinha porque lista execução EM ANDAMENTO; aqui seria desperdício.
 */

import { useQuery } from '@tanstack/react-query';
import type { NodeRunInputsResponse, NodeRunListResponse } from '@troia-v3/types';

import { workflowsApi } from '../services';

/** Quantas execuções a lista da modal pede. O teto do servidor é 50. */
export const NODE_RUNS_LIMIT = 20;

export const nodeRunKeys = {
  all: ['workflow-node-runs'] as const,
  lists: () => [...nodeRunKeys.all, 'list'] as const,
  list: (workflowId: string, nodeId: string, limit: number) =>
    [...nodeRunKeys.lists(), workflowId, nodeId, limit] as const,
  inputs: () => [...nodeRunKeys.all, 'inputs'] as const,
  input: (executionId: string, nodeId: string) =>
    [...nodeRunKeys.inputs(), executionId, nodeId] as const,
};

/** Execuções em que o nó recebeu contexto (mais recentes primeiro). */
export function useNodeRuns(
  workflowId: string,
  nodeId: string,
  options: { enabled: boolean }
) {
  return useQuery<NodeRunListResponse, Error>({
    queryKey: nodeRunKeys.list(workflowId, nodeId, NODE_RUNS_LIMIT),
    queryFn: () => workflowsApi.getNodeRuns(workflowId, nodeId, NODE_RUNS_LIMIT),
    enabled: options.enabled && workflowId !== '' && nodeId !== '',
    staleTime: 30 * 1000,
  });
}

/** O que chegou ao nó em cada passagem de UMA execução. */
export function useNodeRunInputs(
  executionId: string | null,
  nodeId: string,
  options: { enabled: boolean }
) {
  return useQuery<NodeRunInputsResponse, Error>({
    queryKey: nodeRunKeys.input(executionId ?? '', nodeId),
    queryFn: () => workflowsApi.getNodeRunInputs(executionId ?? '', nodeId),
    enabled: options.enabled && executionId !== null && nodeId !== '',
    staleTime: 5 * 60 * 1000,
  });
}
